import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";
import { expectExternalPop } from "./sheet-history";

// History entries this tab pushed to open a view, by Next's per-entry key.
// Closing one of those goes back, so Back and the close button agree; a view
// opened from a link (no entry of ours) closes by replacing the URL instead.
const pushedKeys = new Set<string>();

const currentKey = (): string | undefined =>
  typeof window === "undefined" ? undefined : window.history.state?.key;

/**
 * A detail view whose open state lives in the URL as `?<param>=<value>`, so it
 * can be linked, and the browser's Back (or an edge swipe) closes it.
 */
export const usePushedView = (param: string) => {
  const router = useRouter();
  const raw = router.query[param];
  const value = typeof raw === "string" ? raw : undefined;
  // useRouter's query is a per-render snapshot, so a second close before the
  // popstate re-render would still see the param and close twice.
  const closing = useRef(false);
  const opening = useRef(false);
  useEffect(() => {
    closing.current = false;
  }, [value]);

  const open = useCallback(
    /**
     * `drop` names params that belong to the screen being left (a thread, a
     * panel) and must not ride into the new one. The entry left behind keeps
     * them, so Back returns to exactly that screen.
     */
    async (next: string, { drop = [] }: { drop?: string[] } = {}) => {
      if (router.query[param] === next || opening.current) return;
      opening.current = true;
      try {
        // Back to an entry replays its own navigation options. Marking the
        // entry we leave as shallow keeps the close from refetching the page
        // (and from running the proxy) when it was a full load or a Link push.
        await router.replace(
          { pathname: router.pathname, query: router.query },
          undefined,
          { shallow: true, scroll: false }
        );
        const query = { ...router.query, [param]: next };
        for (const key of drop) delete query[key];
        await router.push({ pathname: router.pathname, query }, undefined, {
          shallow: true,
          scroll: false,
        });
        const key = currentKey();
        if (key) pushedKeys.add(key);
      } finally {
        opening.current = false;
      }
    },
    [router, param]
  );

  const close = useCallback(() => {
    if (router.query[param] === undefined || closing.current) return;
    closing.current = true;
    const key = currentKey();
    if (key && pushedKeys.has(key)) {
      pushedKeys.delete(key);
      expectExternalPop();
      router.back();
      return;
    }
    const { [param]: _closed, ...query } = router.query;
    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
      scroll: false,
    });
  }, [router, param]);

  // Points the open view at another value without adding a history entry.
  // The entry keeps its key, so Back still closes the view.
  const replace = useCallback(
    (next: string) => {
      if (router.query[param] === undefined || router.query[param] === next) {
        return;
      }
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, [param]: next },
        },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [router, param]
  );

  return { value, isOpen: value !== undefined, open, close, replace };
};
